import { Dialog } from '@capacitor/dialog';
import { useTranslate } from '@tolgee/react';
import { useAuth } from 'lincd-auth/lib/hooks/useAuth';
import { Team } from 'lincd-irlcg/lib/shapes/Team';
import { Button } from 'lincd-mui-base/lib/components/Button';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container } from '../layout/Container';
import { DefaultLayout } from '../layout/DefaultLayout';
import { ROUTES } from '../routes';
import './JoinTeam.scss';
import style from './JoinTeam.scss.json';
import { Player } from 'lincd-irlcg/lib/shapes/Player';
import Header from '../components/molecules/Header';
import JoinInputButton from '../components/molecules/JoinInputButton';
import ConferenceCard from '../components/molecules/ConferenceCard';
import { EventTeam } from 'lincd-irlcg/lib/shapes/EventTeam';
import { UserAccount } from 'profile-plus/lib/shapes/UserAccount';
import { withRetry } from 'lincd-irlcg/lib/utils/helper';

function JoinTeam() {
  let { t } = useTranslate();
  let prefix = 'joinTeam';
  const navigate = useNavigate();
  const user = useAuth().user as Player;
  const [loading, setLoading] = useState(false);
  const [eventTeams, setEventTeams] = useState([]);
  const auth = useAuth<Player, UserAccount>();

  let isEventTeam = user?.isEventMode();

  const getAllEventTeams = async () => {
    setLoading(true);
    try {
      const eventTeams = await EventTeam.getAllEventTeams();
      setEventTeams(eventTeams || []);
    } catch (error) {
      console.warn('Failed to fetch event teams:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllEventTeams();
  }, []);

  // add user to team trial / event
  const onSelectButton = async (type: 'trial' | 'event') => {
    setLoading(true);
    try {
      const result = await withRetry(() =>
        Team.addUserToTeam(type, user, false),
      );
      if (result.team) {
        try {
          await withRetry(() => Team.addUserToAction(user));

          // set user.profileSetupCompleted to true after the user is added to a team
          // to prevent the user from navigating back or manually entering the URL to the home page
          if (user) {
            user.profileSetupCompleted = true;
          }
          navigate(ROUTES.index.path);
        } catch (err) {
          console.error('Error adding user to action:', err);
          setLoading(false);
          await Dialog.alert({
            title: t(prefix + '.actionErrorTitle', 'Action Error'),
            message: t(
              prefix + '.actionErrorMessage',
              'Hmm, things are a little crowded right now. We were not able to add you to the team. Please try again in a moment.',
            ),
          });
        }
      } else if (result.error) {
        await Dialog.alert({
          title: 'Something went wrong',
          message: result.error.message,
        });
      } else {
        await Dialog.alert({
          title: t(prefix + '.teamNotFoundTitle', 'Team not found'),
          message: t(
            prefix + '.teamNotFoundMessage',
            `We couldn’t locate the ${type} team. Could you double-check the name and try again?`,
          ),
        });
      }
    } catch (err) {
      console.error(`Error joining ${type}:`, err);
      await Dialog.alert({
        title: t(prefix + '.joinErrorTitle', 'Join Error'),
        message: t(
          prefix + '.joinErrorMessage',
          `Hmm, things are a little crowded right now. We were not able to add you to the team. Please try again in a moment.`,
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const onJoinEventTeam = async (teamId: string) => {
    setLoading(true);
    try {
      const result = await withRetry(() =>
        Team.addUserToTeam(parseInt(teamId), user, false, true),
      );
      if (result.team) {
        user.profileSetupCompleted = true;
        user.currentTeam = result.team;
        navigate(ROUTES.attendence_select.path);
      } else {
        await Dialog.alert({
          title: 'Something went wrong',
          message: result?.error?.message,
        });
      }
    } catch (err) {
      console.error('Error joining event team:', err);
      await Dialog.alert({
        title: t(prefix + '.joinEventErrorTitle', 'Join Event Error'),
        message: t(
          prefix + '.joinEventErrorMessage',
          'We couldn’t locate the event team. Could you double-check the name and try again?',
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DefaultLayout>
      <Header
        title={t(prefix + '.header', 'GET STARTED')}
        inverted={true}
        backButton={user.profileSetupCompleted}
      />
      <Container maxWidth={'sm'}>
        <div
          className={style.JoinTeamContainer}
          aria-labelledby={t(
            prefix + '.containerLabel',
            'Peace Game Container',
          )}
        >
          <JoinInputButton
            description={'Put in your Peace Team Number here.'}
          />

          {/* if user doesnt join to any team, show event button */}
          {/* {!user.currentTeam && (
            <>
              <h2 className={style.subText}>
                {t(prefix + '.separator1', 'Or')}
              </h2>
              <Button
                variant={'filled'}
                className={style.buttonJoinTeam}
                color={'primary'}
                onClick={() => onSelectButton('event')}
                fullWidth={true}
                disabled={loading}
                aria-label={t(
                  prefix + '.peaceGameEventButton',
                  'Peace Game Event button',
                )}
              >
                {t(
                  prefix + '.peaceGameEventButtonText',
                  'Join Peace Day Event',
                )}
              </Button>
              <p
                aria-labelledby={t(
                  prefix + '.peaceGameEventInfo',
                  'Peace Game Event Info',
                )}
              >
                {t(
                  prefix + '.peaceGameEventInfoText',
                  'Choose this option if you are participating in a Peace Day event.',
                )}
              </p>
            </>
          )} */}

          <>
            {eventTeams && eventTeams.length > 0 && (
              <>
                <h2 className={style.subText}>
                  {t(prefix + '.separator1', 'Or')}
                </h2>
                <div className={style.cardContainer}>
                  {eventTeams.map((team) => (
                    <ConferenceCard
                      key={team.id}
                      id={team.id}
                      name={team.name}
                      description={team.description}
                      image={team.image}
                      loading={loading}
                      onClick={() => onJoinEventTeam(team.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </>

          {/* if user doesnt join to any team or is in event team, show trial button */}
          {(!user?.currentTeam || isEventTeam) && (
            <>
              <h2 className={style.subText}>
                {t(prefix + '.separator2', 'Or')}
              </h2>
              <Button
                variant={'filled'}
                className={style.buttonJoinTeam}
                color={'primary'}
                onClick={() => onSelectButton('trial')}
                fullWidth={true}
                disabled={loading}
                aria-label={t(
                  prefix + '.peaceGameTrialButton',
                  'Peace Game Trial button',
                )}
              >
                {t(
                  prefix + '.peaceGameTrialButtonText',
                  'Browse Sample Peace Actions',
                )}
              </Button>
              <p
                aria-labelledby={t(
                  prefix + '.peaceGameTrialInfo',
                  'Peace Game Trial Info',
                )}
              >
                {t(
                  prefix + '.peaceGameTrialInfoText',
                  'Choose this option if you are not on a Peace Team yet and wish to learn more about the Peace Game App.',
                )}
              </p>
            </>
          )}
        </div>
      </Container>
    </DefaultLayout>
  );
}

export default JoinTeam;
